import UserService from "../services/UserService";
import { can, openNotification } from "../utils";
import { setRoleNames } from "../auth/roleAccess";

export const IMPERSONATOR_SESSION_KEY = "impersonatorSession";

const SESSION_KEYS = [
  "accessToken",
  "refreshToken",
  "accessTokenExpiredAt",
  "refreshTokenExpiredAt",
  "firstName",
  "lastName",
  "id",
  "salesTarget",
  "email",
  "profilePhoto",
  "role",
  "roleName",
  "roleNames",
  "gsmPortalEmployeeId",
  "permissions",
] as const;

type SessionKey = (typeof SESSION_KEYS)[number];

/** Giriş sonrası ilk sayfa: birleşik yetkilere göre (rol sırasına bağlı değil). */
export function getDefaultPathAfterLogin(): string {
  if (can("account-management-customers:getAll")) {
    return "/account-management-customers";
  }
  if (can("mongerlab-customers:getAll")) {
    return "/mongerlab-customers";
  }
  if (can("customers:getAll") || can("customers:getAllForCtm")) {
    return "/customers";
  }
  if (can("prospect-customers:getAll")) {
    return "/prospect-customers";
  }
  if (can("amazon-suspended-applicants:getAll")) {
    return "/amazon-suspended-applicants";
  }
  if (can("mas-customers:getAll")) {
    return "/mas-customers";
  }
  if (can("hi5-customers:getAll")) {
    return "/hi5-customers";
  }
  return "/";
}

/** @deprecated getDefaultPathAfterLogin kullanın */
export function getDefaultPathForRole(roleName: string | null | undefined): string {
  if (roleName === "SUCCESS_TEAM_REPORTER") {
    return "/edu-learners";
  }
  if (roleName === "ONLY_SEE_CUSTOMER") {
    return "/customers";
  }
  return getDefaultPathAfterLogin();
}

export function captureSession(): string {
  const session: Partial<Record<SessionKey, string | null>> = {};
  SESSION_KEYS.forEach((key) => {
    session[key] = localStorage.getItem(key);
  });
  return JSON.stringify(session);
}

export function restoreSession(serializedSession: string): string {
  const session = JSON.parse(serializedSession) as Partial<Record<SessionKey, string | null>>;
  SESSION_KEYS.forEach((key) => {
    const value = session[key];
    if (value == null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  });
  return localStorage.getItem("roleName") || "";
}

export function clearImpersonatorSession(): void {
  localStorage.removeItem(IMPERSONATOR_SESSION_KEY);
}

export function normalizeGateHintEmail(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

export function getCurrentGateSessionEmail(): string {
  return normalizeGateHintEmail(localStorage.getItem("email"));
}

/** Gate SSO: hedef e-posta mevcut oturumla aynı değilse local oturumu temizle. */
export function prepareGateLoginSession(hintEmail: string | null): void {
  const hint = normalizeGateHintEmail(hintEmail);
  const current = getCurrentGateSessionEmail();
  if (hint && current && hint === current) {
    return;
  }
  if (!localStorage.getItem("accessToken")) {
    return;
  }
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function isImpersonating(): boolean {
  return localStorage.getItem(IMPERSONATOR_SESSION_KEY) != null;
}

function storeTokens(response: { data: Record<string, string> }): void {
  const { accessToken, refreshToken, accessTokenExpiredAt, refreshTokenExpiredAt } = response.data;
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("accessTokenExpiredAt", accessTokenExpiredAt);
  localStorage.setItem("refreshTokenExpiredAt", refreshTokenExpiredAt);
}

function clearAuthTokens(): void {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessTokenExpiredAt");
  localStorage.removeItem("refreshTokenExpiredAt");
}

export function applyUserProfileFromMe(data: any): string {
  const roles = data.roles || [];
  const roleNameList: string[] = roles
    .map((role: any) => role.name)
    .filter((name: unknown): name is string => typeof name === "string" && name.length > 0);

  setRoleNames(roleNameList);

  const adminRole = roles.find((role: any) => role.name === "ADMIN");
  const selectedRoleName =
    typeof data.selectedRole === "string" && roleNameList.includes(data.selectedRole)
      ? data.selectedRole
      : null;
  const displayRole =
    (selectedRoleName && roles.find((role: any) => role.name === selectedRoleName)) ||
    adminRole ||
    roles[0];

  localStorage.setItem("firstName", data.firstName);
  localStorage.setItem("lastName", data.lastName);
  localStorage.setItem("id", String(data.id));
  localStorage.setItem("salesTarget", data.salesTarget);
  localStorage.setItem("email", data.username);
  localStorage.setItem("profilePhoto", data.profilePhoto || 1);
  localStorage.setItem("role", displayRole?.description || "");
  localStorage.setItem("roleName", displayRole?.name || "");
  localStorage.setItem("gsmPortalEmployeeId", data.gsmPortalEmployeeId);

  const permissionNames = Array.from(
    new Set(
      roles.flatMap((role: any) =>
        (role.permissions || []).map((permission: any) => permission.name)
      )
    )
  );
  localStorage.setItem("permissions", JSON.stringify(permissionNames));

  return displayRole?.name || "";
}

export async function applyAuthTokensAndRedirect(tokenResponse: {
  data: Record<string, string>;
}): Promise<void> {
  storeTokens(tokenResponse);
  try {
    const meResponse = await UserService.getMe();
    applyUserProfileFromMe(meResponse.data);
    window.location.assign(getDefaultPathAfterLogin());
  } catch (error: any) {
    clearAuthTokens();
    const message =
      error?.response?.data?.message ||
      "Oturum bilgisi alınamadı. Role users:getAuthUser yetkisi ekleyin.";
    openNotification("error", "Giriş hatası", message);
    throw error;
  }
}

export function completeLoginSession(
  tokenResponse: { data: Record<string, string> },
  navigate: (path: string) => void,
  setIsLoggedIn: (value: boolean) => void,
  isLoggedIn: boolean
): void {
  storeTokens(tokenResponse);
  UserService.getMe()
    .then((meResponse: any) => {
      applyUserProfileFromMe(meResponse.data);
      navigate(getDefaultPathAfterLogin());
      setIsLoggedIn(!isLoggedIn);
    })
    .catch((error: any) => {
      clearAuthTokens();
      const message =
        error?.response?.data?.message ||
        "Oturum bilgisi alınamadı. Role users:getAuthUser yetkisi ekleyin.";
      openNotification("error", "Giriş hatası", message);
    });
}
